import { useEffect, useRef, useState } from 'react';
import {
	Drawer,
	Button,
	NumberInput,
	TextInput,
	Select,
	LoadingOverlay,
	Flex,
	DrawerCloseButtonProps,
} from '@mantine/core';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { SecurityIssuedIn, SecurityType } from '@dealstack/shared/lib/generated';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { SECURITY_TYPE_OPTIONS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import { FormField, ButtonStyles, disabledInputStyles, HelperText } from './SecurityDrawer.styles';
import { SecurityFormData } from './SecurityTypes';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

interface Props {
	opened: boolean;
	onClose: () => void;
	securityData: SecurityFormData;
	onSave: (security: SecurityFormData) => void;
	onRemove?: (securityId: string) => void;
	isLoading?: boolean;
	context: 'mipCreate' | 'mipManage';
	hasAdminPermissions?: boolean;
}

export const SecurityDrawer = ({
	opened,
	onClose,
	securityData,
	onSave,
	onRemove,
	isLoading = false,
	context,
	hasAdminPermissions,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();

	const isEditMode = Boolean(securityData?.id);

	const [originalIssuedIn, setOriginalIssuedIn] = useState<SecurityIssuedIn | undefined>(
		undefined
	);

	useEffect(() => {
		if (securityData?.issuedIn) {
			setOriginalIssuedIn(securityData.issuedIn);
		}
	}, [securityData]);

	const methods = useForm<SecurityFormData>({
		defaultValues: {
			type: SecurityType.ORDINARY_SHARE,
			name: '',
			votesPerUnit: 1,
			nominalValue: 0,
			isSubjectToVesting: false,
			fairMarketValuePerUnit: null,
			issuedIn: SecurityIssuedIn.UNITS,
		},
	});

	const { handleSubmit, reset, register, watch, setValue } = methods;

	const wasOpened = useRef(false);

	useEffect(() => {
		// Reset only when the drawer opens; parent re-renders while open must not wipe user input.
		if (opened && !wasOpened.current) {
			reset(securityData);
		}
		wasOpened.current = opened;
	}, [securityData, reset, opened]);

	const onSubmit = (data: SecurityFormData) => {
		const securityIssuedInValue = data.issuedIn || SecurityIssuedIn.UNITS;

		const submissionData = {
			...data,
			issuedIn: securityIssuedInValue,
			id: securityData?.id,
		};

		onSave(submissionData);
	};

	const handleDelete = () => {
		if (securityData?.id && onRemove) {
			onRemove(securityData.id);
		}
	};

	const securityType = watch('type');
	const votesPerUnit = watch('votesPerUnit');
	const nominalValue = watch('nominalValue');
	const fairMarketValuePerUnit = watch('fairMarketValuePerUnit');

	const securityIssuedIn = watch('issuedIn');

	const shouldEnableValuePerUnit =
		securityIssuedIn !== SecurityIssuedIn.CURRENCY &&
		originalIssuedIn !== SecurityIssuedIn.CURRENCY;

	// '' rather than undefined: an undefined value prop flips Mantine's NumberInput
	// into uncontrolled mode and it silently falls back to its own stale state.
	const getNumberValue = (value: number | null | undefined): number | '' => {
		return typeof value === 'number' ? value : '';
	};

	// fixedDecimalScale re-pads the decimals on every keystroke, so backspacing over
	// them only moves the caret. Pad the resting display, not the one being edited.
	const [focusedField, setFocusedField] = useState<keyof SecurityFormData | null>(null);
	const decimalFieldProps = (field: keyof SecurityFormData) => ({
		decimalScale: 2,
		fixedDecimalScale: focusedField !== field,
		onFocus: () => setFocusedField(field),
		onBlur: () => setFocusedField(null),
	});

	const getDrawerTitle = () => {
		if (isEditMode) {
			return tCommonUi('securities.drawer.edit.title');
		} else {
			return context === 'mipCreate'
				? tCommonUi('securities.drawer.add.title')
				: tCommonUi('securities.drawer.create.title');
		}
	};

	const getSubmitButtonText = () => {
		if (isEditMode) {
			return tCommonUi('securities.drawer.save.button');
		} else {
			return context === 'mipCreate'
				? tCommonUi('securities.drawer.add.button')
				: tCommonUi('securities.drawer.create.button');
		}
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			size="md"
			transitionProps={drawerTransitionProps}
			title={getDrawerTitle()}
			closeButtonProps={
				{
					'data-testid': TEST_ELEMENT_IDS.SECURITIES_CLOSE_BUTTON,
				} as DrawerCloseButtonProps
			}
		>
			<Flex direction="column" h="100%" mih={400}>
				<LoadingOverlay visible={isLoading} overlayProps={{ blur: 2 }} />
				<FormProvider {...methods}>
					<form
						onSubmit={handleSubmit(onSubmit)}
						style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
					>
						<div style={{ flex: 1 }}>
							<FormField>
								<Select
									label={tCommonUi('securities.drawer.type.label')}
									placeholder={tCommonUi('securities.drawer.type.placeholder')}
									size="md"
									required
									allowDeselect={false}
									data={SECURITY_TYPE_OPTIONS}
									value={securityType}
									onChange={(value) => setValue('type', value as SecurityType)}
									rightSection={<Icon variant="caret-down" size="lg" />}
									data-testid={TEST_ELEMENT_IDS.TYPE_OF_SECURITY}
									disabled={isLoading}
								/>
							</FormField>

							<FormField>
								<TextInput
									label={tCommonUi('securities.drawer.name.label')}
									placeholder={tCommonUi('securities.drawer.name.placeholder')}
									size="md"
									required
									{...register('name')}
									data-testid={TEST_ELEMENT_IDS.NAME_OF_SECURITY}
									disabled={isLoading}
								/>
							</FormField>

							<FormField>
								<NumberInput
									label={tCommonUi('securities.drawer.votes.label')}
									placeholder={tCommonUi('securities.drawer.votes.placeholder')}
									size="md"
									value={getNumberValue(votesPerUnit)}
									onChange={(value) =>
										setValue(
											'votesPerUnit',
											typeof value === 'number' ? value : null
										)
									}
									disabled={isLoading || !shouldEnableValuePerUnit}
									min={0}
									styles={
										!shouldEnableValuePerUnit ? disabledInputStyles : undefined
									}
									data-testid={TEST_ELEMENT_IDS.VOTES_PER_SHARE_OF_SECURITY}
								/>
								{!shouldEnableValuePerUnit && (
									<HelperText>
										{tCommonUi('securities.votes_per_sharenot_applicable')}
									</HelperText>
								)}
							</FormField>

							<FormField>
								<NumberInput
									label={tCommonUi('securities.drawer.nominal_value.label')}
									size="md"
									placeholder={tCommonUi(
										'securities.drawer.nominal_value.placeholder'
									)}
									value={getNumberValue(nominalValue)}
									onChange={(value) =>
										setValue(
											'nominalValue',
											typeof value === 'number' ? value : null
										)
									}
									{...decimalFieldProps('nominalValue')}
									disabled={isLoading}
									min={0}
									data-testid={TEST_ELEMENT_IDS.NOMINAL_VALUE_OF_SECURITY}
								/>
							</FormField>

							<FormField>
								<NumberInput
									label={tCommonUi('securities.value_per_unit')}
									placeholder={tCommonUi('securities.value_per_unit_placeholder')}
									size="md"
									value={getNumberValue(fairMarketValuePerUnit)}
									onChange={(value) =>
										setValue(
											'fairMarketValuePerUnit',
											typeof value === 'number' ? value : null
										)
									}
									{...decimalFieldProps('fairMarketValuePerUnit')}
									disabled={isLoading || !shouldEnableValuePerUnit}
									min={0}
									styles={
										!shouldEnableValuePerUnit ? disabledInputStyles : undefined
									}
								/>
								{!shouldEnableValuePerUnit && (
									<HelperText>
										{tCommonUi('securities.value_per_unit_not_applicable')}
									</HelperText>
								)}
							</FormField>

							<FormField>
								<AdvancedSwitch
									selectedValue={watch('isSubjectToVesting')}
									onChange={(e) => setValue('isSubjectToVesting', !!e)}
									rightText={tCommonUi(
										'securities.drawer.subject_to_vesting.label'
									)}
									disabled={isLoading}
									data-testid={TEST_ELEMENT_IDS.SUBJECT_TO_VESTING_TOGGLE}
									selectedColor="dark.8"
									unselectedColor="dark.8"
									unselectedFontWeight={600}
									unselectedLetterSpacing="0"
								/>
							</FormField>
						</div>

						<Flex
							direction="column"
							align="flex-start"
							gap="1rem"
							mt="auto"
							pt="1.5rem"
						>
							<Button
								type="submit"
								fullWidth
								color="blue"
								loading={isLoading}
								sx={ButtonStyles}
								data-testid={TEST_ELEMENT_IDS.SECURITIES_SAVE_BUTTON}
							>
								{getSubmitButtonText()}
							</Button>

							{hasAdminPermissions && isEditMode && onRemove && (
								<Button
									variant="subtle"
									color="red"
									onClick={handleDelete}
									fullWidth
									disabled={isLoading}
									sx={ButtonStyles}
									data-testid={TEST_ELEMENT_IDS.SECURITIES_DELETE_BUTTON}
								>
									{tCommonUi('securities.drawer.delete.button')}
								</Button>
							)}
						</Flex>
					</form>
				</FormProvider>
			</Flex>
		</Drawer>
	);
};
